/**
 * Journal commands — the write path for a page of blocks about one ferment.
 *
 * Two groups, both of them shapes this package already has:
 *
 * | Group | Commands | Shape it follows |
 * | --- | --- | --- |
 * | Lifecycle | `create` · `update` · `delete` · `restore` | the blend four |
 * | Blocks | `addBlock` · `setBlock` · `moveBlock` · `removeBlock` | list add/update/remove, plus a move |
 *
 * Three things are worth saying out loud.
 *
 * 1. **`setBlock` replaces a block whole, and refuses a kind change.** One
 *    command covers every edit a block takes — the prose, the date, the eye
 *    toggle — because a block is small enough to send whole and a per-field
 *    change set for a six-member union is six schemas that would drift. What it
 *    will not do is turn a photo into a measurement: the id is the same, so
 *    nothing would be *lost*, but a block that silently changed kind would take
 *    its R2 object with it and leave bytes nothing points at. A `conflict` is
 *    the honest answer; removing and adding is how a brewer really does it.
 * 2. **`moveBlock` is a command, not a view setting.** Order is the brewer's
 *    own (D-J2) — an introduction sits at the top of a page whose first entry is
 *    a month old — so a "sort by date" button dispatches moves rather than
 *    changing how the stack renders. That is also what makes the sort undoable.
 * 3. **`removeBlock` has no inverse for a photo or a file block**, for exactly
 *    the reason `batch.removePhoto` has none: the descriptor is not the data,
 *    the R2 object is, and the route that removes one deletes it. An inverse
 *    would put a tile, or a download link, back over bytes that are gone. Every
 *    other block kind *is* its data, so removing one inverts to an `addBlock` at
 *    the position it held.
 *
 * Every command that touches an existing journal goes through `withJournal`, so
 * `updatedAt` is stamped from the envelope in one place.
 */

import {
  blankJournalDoc,
  JOURNAL_MAX_BLOCKS,
  type JournalBlock,
  type JournalDoc,
} from '../../document/journal';
import type { WorkspaceState } from '../../document/workspace';
import { defineCommand, inverseCommand } from '../definition';
import { commandError } from '../envelope';
import { quoted } from '../format';
import {
  dropJournal,
  indexOfId,
  insertAt,
  mergeDefined,
  priorValues,
  putJournal,
  removeAt,
  replaceAt,
  withJournal,
} from '../helpers';
import * as p from '../payloads';

/**
 * Write a batch id onto a batch block that names none.
 *
 * D-M5's half of the move: an old `journal.update` carrying `batchId` meant
 * "this whole page is about that batch", and the blocks it meant it about are
 * the ones still unstamped. A block that already names a batch is the brewer's
 * own choice and is left alone.
 */
function stampBatch(block: JournalBlock, batchId: string): JournalBlock {
  if (block.type !== 'batch-recipe' && block.type !== 'batch-telemetry') return block;
  return block.batchId === null ? { ...block, batchId } : block;
}

/** Look a journal up for a summary, tolerating its absence (summaries never throw). */
function journalName(state: WorkspaceState, journalId: string): string {
  return state.journals[journalId]?.name ?? journalId;
}

// ---------------------------------------------------------------------------
// Lifecycle
// ---------------------------------------------------------------------------

/** Create a journal, optionally by duplicating an existing one. */
export const journalCreate = defineCommand({
  type: 'journal.create',
  payload: p.journalCreatePayloadSchema,
  apply: (state, command) => {
    const { journalId, name, from } = command.payload;
    if (state.journals[journalId] !== undefined) {
      return {
        ok: false,
        error: commandError('conflict', `A journal with id "${journalId}" already exists.`, {
          commandId: command.commandId,
          commandType: command.type,
        }),
      };
    }
    const doc: JournalDoc =
      from === undefined
        ? blankJournalDoc({ id: journalId, name, timestamp: command.timestamp })
        : {
            ...from,
            id: journalId,
            name,
            createdAt: command.timestamp,
            updatedAt: command.timestamp,
          };
    return { ok: true, state: putJournal(state, doc) };
  },
  invert: (_state, command) =>
    inverseCommand(
      command,
      'journal.delete',
      { journalId: command.payload.journalId },
      command.timestamp,
    ),
  summarize: (_state, command) => `Create journal ${quoted(command.payload.name)}`,
});

/**
 * Rename a journal, or set its tags.
 *
 * The third change this may carry is `batchId`, and it is not a field of a
 * journal: the link lives on the block now (D-M1). It is here because command
 * logs and outboxes hold updates that name it, and D-M5 asks that a replay of
 * those cannot fail. Such a change applies as the stamping the document's own
 * migration does — the id goes onto every batch block that names none — and it
 * does not earn an inverse. A compatibility path nothing sends any more is not
 * a thing a brewer can have just done, so there is nothing for ⌘Z to undo; a
 * `null` inverse is how `workspace-store.ts` is told to leave the undo stack
 * alone.
 */
export const journalUpdate = defineCommand({
  type: 'journal.update',
  payload: p.journalUpdatePayloadSchema,
  apply: (state, command) =>
    withJournal(state, command, command.payload.journalId, (journal) => {
      const { batchId, ...changes } = command.payload.changes;
      const merged = mergeDefined(journal, changes);
      return batchId === undefined || batchId === null
        ? merged
        : { ...merged, blocks: merged.blocks.map((block) => stampBatch(block, batchId)) };
    }),
  invert: (state, command) => {
    const journal = state.journals[command.payload.journalId];
    if (journal === undefined || command.payload.changes.batchId !== undefined) return null;
    return inverseCommand(
      command,
      'journal.update',
      {
        journalId: journal.id,
        changes: priorValues(journal, command.payload.changes),
      },
      journal.updatedAt,
    );
  },
  summarize: (state, command) => {
    const keys = Object.keys(command.payload.changes).sort().join(', ');
    return `Update journal ${quoted(journalName(state, command.payload.journalId))}${
      keys === '' ? '' : ` (${keys})`
    }`;
  },
});

/** Delete a journal. */
export const journalDelete = defineCommand({
  type: 'journal.delete',
  payload: p.journalDeletePayloadSchema,
  apply: (state, command) => {
    const journal = state.journals[command.payload.journalId];
    if (journal === undefined) {
      return {
        ok: false,
        error: commandError('not-found', `No journal with id "${command.payload.journalId}".`, {
          commandId: command.commandId,
          commandType: command.type,
        }),
      };
    }
    return { ok: true, state: dropJournal(state, command.payload.journalId) };
  },
  invert: (state, command) => {
    const journal = state.journals[command.payload.journalId];
    return journal === undefined
      ? null
      : inverseCommand(command, 'journal.restore', { journal }, journal.updatedAt);
  },
  summarize: (state, command) =>
    `Delete journal ${quoted(journalName(state, command.payload.journalId))}`,
});

/** Insert a journal verbatim — an upsert, and the inverse of `journal.delete`. */
export const journalRestore = defineCommand({
  type: 'journal.restore',
  payload: p.journalRestorePayloadSchema,
  apply: (state, command) => ({ ok: true, state: putJournal(state, command.payload.journal) }),
  invert: (state, command) => {
    const prior = state.journals[command.payload.journal.id];
    return prior === undefined
      ? inverseCommand(
          command,
          'journal.delete',
          { journalId: command.payload.journal.id },
          command.timestamp,
        )
      : inverseCommand(command, 'journal.restore', { journal: prior }, prior.updatedAt);
  },
  summarize: (_state, command) => `Restore journal ${quoted(command.payload.journal.name)}`,
});

// ---------------------------------------------------------------------------
// Blocks
// ---------------------------------------------------------------------------

/** Add one block to the stack, at `index` or appended. */
export const journalAddBlock = defineCommand({
  type: 'journal.addBlock',
  payload: p.journalAddBlockPayloadSchema,
  apply: (state, command) =>
    withJournal(state, command, command.payload.journalId, (journal) => {
      const { block, index } = command.payload;
      if (indexOfId(journal.blocks, 'blockId', block.blockId) !== -1) {
        return commandError(
          'conflict',
          `A block with id "${block.blockId}" is already on this journal.`,
        );
      }
      if (journal.blocks.length >= JOURNAL_MAX_BLOCKS) {
        return commandError('conflict', `A journal holds at most ${JOURNAL_MAX_BLOCKS} blocks.`);
      }
      return { ...journal, blocks: insertAt(journal.blocks, block, index) };
    }),
  invert: (state, command) => {
    const prior = state.journals[command.payload.journalId];
    return prior === undefined
      ? null
      : inverseCommand(
          command,
          'journal.removeBlock',
          { journalId: prior.id, blockId: command.payload.block.blockId },
          prior.updatedAt,
        );
  },
  summarize: (state, command) =>
    `Add a ${command.payload.block.type} block to ${quoted(
      journalName(state, command.payload.journalId),
    )}`,
});

/** Replace one block verbatim. Refuses a block whose kind differs from the stored one. */
export const journalSetBlock = defineCommand({
  type: 'journal.setBlock',
  payload: p.journalSetBlockPayloadSchema,
  apply: (state, command) =>
    withJournal(state, command, command.payload.journalId, (journal) => {
      const { block } = command.payload;
      const index = indexOfId(journal.blocks, 'blockId', block.blockId);
      const current = journal.blocks[index];
      if (current === undefined) {
        return commandError('not-found', `No block with id "${block.blockId}" on this journal.`);
      }
      if (current.type !== block.type) {
        return commandError(
          'conflict',
          `Block "${block.blockId}" is a ${current.type} block; remove it to add a ${block.type} one.`,
        );
      }
      return { ...journal, blocks: replaceAt(journal.blocks, index, block) };
    }),
  invert: (state, command) => {
    const prior = state.journals[command.payload.journalId];
    const current =
      prior?.blocks[indexOfId(prior.blocks, 'blockId', command.payload.block.blockId)];
    return prior === undefined || current === undefined
      ? null
      : inverseCommand(
          command,
          'journal.setBlock',
          { journalId: prior.id, block: current },
          prior.updatedAt,
        );
  },
  summarize: (state, command) =>
    `Edit a ${command.payload.block.type} block in ${quoted(
      journalName(state, command.payload.journalId),
    )}`,
});

/** Move one block to another position in the stack. */
export const journalMoveBlock = defineCommand({
  type: 'journal.moveBlock',
  payload: p.journalMoveBlockPayloadSchema,
  apply: (state, command) =>
    withJournal(state, command, command.payload.journalId, (journal) => {
      const { blockId, index } = command.payload;
      const from = indexOfId(journal.blocks, 'blockId', blockId);
      const block = journal.blocks[from];
      if (block === undefined) {
        return commandError('not-found', `No block with id "${blockId}" on this journal.`);
      }
      return { ...journal, blocks: insertAt(removeAt(journal.blocks, from), block, index) };
    }),
  invert: (state, command) => {
    const prior = state.journals[command.payload.journalId];
    if (prior === undefined) return null;
    const from = indexOfId(prior.blocks, 'blockId', command.payload.blockId);
    return from === -1
      ? null
      : inverseCommand(
          command,
          'journal.moveBlock',
          { journalId: prior.id, blockId: command.payload.blockId, index: from },
          prior.updatedAt,
        );
  },
  summarize: (state, command) =>
    `Move a block in ${quoted(journalName(state, command.payload.journalId))}`,
});

/**
 * Take one block off the stack.
 *
 * Invertible for every kind but the two that name bytes, `photo` and `file`,
 * and the exception is the one `batch.removePhoto` documents at length: their
 * data is an R2 object, the route that removes the block deletes it, and an
 * inverse would restore a tile — or a download link — pointing at nothing.
 * `workspace-store.ts` reads a `null` inverse as "this step does not go on the
 * undo stack", so ⌘Z after removing a photograph undoes whatever came before it,
 * and the confirmation in the UI is what asks first.
 */
export const journalRemoveBlock = defineCommand({
  type: 'journal.removeBlock',
  payload: p.journalRemoveBlockPayloadSchema,
  apply: (state, command) =>
    withJournal(state, command, command.payload.journalId, (journal) => {
      const index = indexOfId(journal.blocks, 'blockId', command.payload.blockId);
      if (index === -1) {
        return commandError(
          'not-found',
          `No block with id "${command.payload.blockId}" on this journal.`,
        );
      }
      return { ...journal, blocks: removeAt(journal.blocks, index) };
    }),
  invert: (state, command) => {
    const prior = state.journals[command.payload.journalId];
    if (prior === undefined) return null;
    const index = indexOfId(prior.blocks, 'blockId', command.payload.blockId);
    const block = prior.blocks[index];
    if (block === undefined || block.type === 'photo' || block.type === 'file') return null;
    return inverseCommand(
      command,
      'journal.addBlock',
      { journalId: prior.id, block, index },
      prior.updatedAt,
    );
  },
  summarize: (state, command) =>
    `Remove a block from ${quoted(journalName(state, command.payload.journalId))}`,
});
